import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";
import { translateAdminError } from "./adminErrors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { adminLogin } = useAdminAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      if (!["super_admin", "admin"].includes(res.data.user.role)) {
        setError("Esta cuenta no tiene acceso al panel de administración.");
        return;
      }
      adminLogin(res.data.token, res.data.user);
      navigate(res.data.user.must_change_password ? "/admin/change-password" : "/admin");
    } catch (err) {
      setError(translateAdminError(err.response?.data?.detail) || "Email o contraseña incorrectos.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="h-12 w-12 rounded-xl bg-[#028CF8] flex items-center justify-center mb-3">
            <ShieldCheck className="h-6 w-6 text-white" />
          </div>
          <h1 className="font-bold text-xl text-slate-900">InfoCityJobs Admin</h1>
          <p className="text-sm text-slate-500">Acceso restringido al equipo interno</p>
        </div>
        {error && (
          <div data-testid="admin-login-error" className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Email</label>
            <input
              data-testid="admin-login-email-input"
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-11 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Contraseña</label>
            <div className="relative">
              <input
                data-testid="admin-login-password-input"
                type={showPassword ? "text" : "password"}
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-11 px-3 pr-10 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]"
              />
              <button
                type="button"
                data-testid="admin-login-toggle-password"
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="text-right">
            <Link to="/admin/forgot-password" data-testid="admin-forgot-password-link" className="text-xs text-[#028CF8] font-medium hover:underline">
              ¿Olvidaste tu contraseña?
            </Link>
          </div>
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full h-11 rounded-lg bg-[#028CF8] hover:bg-[#0270C5] text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Iniciar sesión"}
          </button>
        </form>
      </div>
    </div>
  );
}
